import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Snackbar,
  Button,
  Box,
  Divider,
  Paper,
  Avatar,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Container
} from '@material-ui/core';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Warning,
  CalendarToday,
  BarChart,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  MonetizationOn,
  Receipt,
  ErrorOutline,
  Business,
  Euro,
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import bonClientService from '../../services/BonClientService';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fa',
    minHeight: '100vh'
  },
  header: {
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    padding: theme.spacing(3),
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    borderRadius: '16px',
    color: 'white',
    boxShadow: '0 8px 32px rgba(34, 109, 104, 0.2)'
  },
  title: {
    fontWeight: 800,
    color: 'white',
    marginBottom: theme.spacing(1),
    fontSize: '2.2rem',
    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  subtitle: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '1.1rem'
  },
  statCard: {
    height: '100%',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    borderRadius: '16px',
    overflow: 'hidden',
    border: 'none',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: '0 16px 40px rgba(0, 0, 0, 0.12)'
    }
  },
  statValue: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    fontSize: '1.8rem'
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase'
  },
  detailsButton: {
    width: '100%',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
    textTransform: 'none',
    padding: theme.spacing(2.5),
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 600,
    background: 'white',
    border: `2px solid ${theme.palette.primary.light}`,
    '&:hover': {
      background: theme.palette.primary.light + '10',
      borderColor: theme.palette.primary.main
    }
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    marginBottom: theme.spacing(4)
  },
  tableHeader: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    '& th': {
      fontWeight: 700,
      color: 'white',
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.05em',
      padding: theme.spacing(2)
    }
  },
  tableRow: {
    '&:nth-of-type(even)': {
      backgroundColor: '#f8f9fa',
    },
    '&:hover': {
      backgroundColor: '#e8f5e9'
    }
  },
  tableCell: {
    padding: theme.spacing(1.5),
    borderBottom: '1px solid #e0e0e0'
  },
  footerMetrics: {
    padding: theme.spacing(4),
    borderRadius: '16px',
    marginTop: theme.spacing(4),
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e0e0e0'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '500px',
    borderRadius: '16px',
    background: '#f8f9fa'
  },
  errorContainer: {
    padding: theme.spacing(4),
    textAlign: 'center',
    borderRadius: '16px',
    backgroundColor: '#ffebee',
    border: '1px solid #ffcdd2',
    margin: theme.spacing(2)
  },
  trendUp: {
    color: '#4caf50',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    fontWeight: 600
  },
  trendDown: {
    color: '#f44336',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    fontWeight: 600
  },
  amountCard: {
    padding: theme.spacing(3),
    height: '100%',
    textAlign: 'center',
    borderRadius: '12px',
    background: 'white',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e0e0e0',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
    }
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#226D68',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(4),
    paddingBottom: theme.spacing(1),
    borderBottom: `3px solid #f26522`,
    display: 'inline-block'
  },
  metricValue: {
    fontWeight: 700,
    fontSize: '1.8rem',
    marginBottom: theme.spacing(0.5)
  },
  metricLabel: {
    fontSize: '0.9rem',
    color: '#666',
    fontWeight: 600
  },
  amountValue: {
    fontWeight: 700,
    fontSize: '1.6rem',
    marginTop: theme.spacing(1),
    color: '#226D68'
  },
  currency: {
    fontSize: '0.9rem',
    color: '#666',
    fontWeight: 600,
    marginTop: theme.spacing(1)
  }
}));

const BonClientStatsDashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await bonClientService.getSyntheseComplet();
      console.log("Données reçues de l'API:", response.data);
      setStats(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des statistiques');
      console.error("Erreur détaillée:", err);
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0,00';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const StatCard = ({ title, value, icon: Icon, color = 'primary', trend, subtitle }) => {
    const colors = {
      primary: { bg: '#E3F2FD', color: '#1976D2' },
      success: { bg: '#E8F5E9', color: '#2E7D32' },
      error: { bg: '#FFEBEE', color: '#D32F2F' },
      warning: { bg: '#FFF3E0', color: '#F57C00' },
      secondary: { bg: '#F3E5F5', color: '#7B1FA2' }
    };

    return (
      <Card className={classes.statCard} elevation={0}>
        <CardContent style={{ padding: theme.spacing(2.5) }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={3}>
              <Avatar style={{ 
                backgroundColor: colors[color].bg,
                color: colors[color].color,
                width: 60,
                height: 60,
                borderRadius: '12px'
              }}>
                <Icon style={{ fontSize: 30 }} />
              </Avatar>
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body2" className={classes.statLabel}>
                {title}
              </Typography>
              <Typography variant="h4" className={classes.statValue}>
                {typeof value === 'number' ? `${formatCurrency(value)} F CFA` : formatNumber(value)}
              </Typography>
              {trend && (
                <Box display="flex" alignItems="center" mt={1}>
                  {trend > 0 ? (
                    <Box className={classes.trendUp}>
                      <TrendingUp fontSize="small" />
                      <Typography variant="body2" style={{ marginLeft: 4 }}>
                        +{trend}%
                      </Typography>
                    </Box>
                  ) : (
                    <Box className={classes.trendDown}>
                      <TrendingDown fontSize="small" />
                      <Typography variant="body2" style={{ marginLeft: 4 }}>
                        {trend}%
                      </Typography>
                    </Box>
                  )}
                  {subtitle && (
                    <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const DataTable = ({ title, data = [], columns, fieldMapping }) => (
    <Box mb={4}>
      <Typography variant="h5" className={classes.sectionTitle}>
        {title}
      </Typography>
      {data.length > 0 ? (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                {columns.map((col, index) => (
                  <TableCell key={index}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, rowIndex) => (
                <TableRow key={rowIndex} hover className={classes.tableRow}>
                  {fieldMapping.map((field, cellIndex) => (
                    <TableCell key={cellIndex} className={classes.tableCell}>
                      {typeof row[field] === 'number' && (field.includes('montant') || field.includes('total') || field.includes('montant')) 
                        ? `${formatCurrency(row[field])} F CFA`
                        : field.includes('commande') || field.includes('date')
                        ? formatDate(row[field])
                        : row[field] || '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper className={classes.amountCard} elevation={1}>
          <Typography variant="body2" color="textSecondary">
            Aucune donnée disponible
          </Typography>
        </Paper>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <Box textAlign="center">
          <CircularProgress size={80} thickness={4} style={{ color: '#226D68' }} />
          <Typography variant="h6" color="textSecondary" style={{ marginTop: 24, fontWeight: 600 }}>
            Chargement des statistiques...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !stats) {
    return (
      <Container maxWidth="md">
        <Paper className={classes.errorContainer} elevation={0}>
          <ErrorOutline style={{ fontSize: 80, color: '#d32f2f', marginBottom: 24 }} />
          <Typography variant="h5" color="error" gutterBottom style={{ fontWeight: 700 }}>
            Erreur de chargement
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph style={{ marginBottom: 32 }}>
            {error || 'Impossible de charger les données'}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={fetchStats}
            size="large"
            style={{ 
              background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
              fontWeight: 600,
              padding: '12px 32px',
              borderRadius: '8px'
            }}
          >
            Réessayer
          </Button>
        </Paper>
      </Container>
    );
  }

  // D'après les logs, stats contient directement les données de getAllBonClientStats()
  const data = stats;

  return (
    <Container maxWidth="xl" className={classes.root}>
      {/* En-tête */}
      {/* <Paper className={classes.header} elevation={0}>
        <Typography variant="h3" className={classes.title}>
          📊 TABLEAU DE BORD STATISTIQUES
        </Typography>
        <Typography variant="h6" className={classes.subtitle}>
          Analyse complète des ventes et impayés - Toutes périodes confondues
        </Typography>
      </Paper> */}

      {/* Cartes de statistiques principales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="TOTAL VENTES"
            value={parseFloat(data.totaux?.global?.montant_total || 0)}
            icon={AttachMoney}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="BONS PAYÉS"
            value={parseFloat(data.totaux?.payes?.montant_total || 0)}
            icon={CheckCircle}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="IMPAYÉS"
            value={parseFloat(data.totaux?.impayes?.montant_total || 0)}
            icon={Warning}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="NOMBRE DE BONS"
            value={data.totaux?.global?.nombre_bons || 0}
            icon={Business}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Détails des montants HT/TVA/TTC */}
      <Typography variant="h5" className={classes.sectionTitle}>
        DÉTAILS DES MONTANTS
      </Typography>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Paper className={classes.amountCard} elevation={0}>
            <Box mb={2} display="flex" justifyContent="center">
              <Avatar style={{ 
                backgroundColor: '#E3F2FD',
                color: '#1976D2',
                width: 64,
                height: 64,
                borderRadius: '16px'
              }}>
                <Receipt style={{ fontSize: 32 }} />
              </Avatar>
            </Box>
            <Typography variant="body1" color="textSecondary" gutterBottom style={{ fontWeight: 600 }}>
              Montant HT Total
            </Typography>
            <Typography variant="h3" className={classes.amountValue}>
              {formatCurrency(data.totaux?.global?.total_ht || 0)}
            </Typography>
            <Typography variant="body2" className={classes.currency}>
              F CFA
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.amountCard} elevation={0}>
            <Box mb={2} display="flex" justifyContent="center">
              <Avatar style={{ 
                backgroundColor: '#F3E5F5',
                color: '#7B1FA2',
                width: 64,
                height: 64,
                borderRadius: '16px'
              }}>
                <MonetizationOn style={{ fontSize: 32 }} />
              </Avatar>
            </Box>
            <Typography variant="body1" color="textSecondary" gutterBottom style={{ fontWeight: 600 }}>
              TVA Totale (18%)
            </Typography>
            <Typography variant="h3" className={classes.amountValue}>
              {formatCurrency(data.totaux?.global?.total_tva || 0)}
            </Typography>
            <Typography variant="body2" className={classes.currency}>
              F CFA
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.amountCard} elevation={0}>
            <Box mb={2} display="flex" justifyContent="center">
              <Avatar style={{ 
                backgroundColor: '#E8F5E9',
                color: '#2E7D32',
                width: 64,
                height: 64,
                borderRadius: '16px'
              }}>
                <Euro style={{ fontSize: 32 }} />
              </Avatar>
            </Box>
            <Typography variant="body1" color="textSecondary" gutterBottom style={{ fontWeight: 600 }}>
              Montant TTC Total
            </Typography>
            <Typography variant="h3" className={classes.amountValue}>
              {formatCurrency(data.totaux?.global?.total_ttc || 0)}
            </Typography>
            <Typography variant="body2" className={classes.currency}>
              F CFA
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Bouton pour afficher plus de détails */}
      <Button
        variant="outlined"
        fullWidth
        endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setShowDetails(!showDetails)}
        className={classes.detailsButton}
        style={{ 
          background: showDetails ? '#226D68' : 'white',
          color: showDetails ? 'white' : '#226D68',
          borderColor: '#226D68'
        }}
      >
        <Box display="flex" alignItems="center">
          <BarChart style={{ marginRight: 12 }} />
          {showDetails ? 'MASQUER LES DÉTAILS COMPLETS' : 'AFFICHER LES DÉTAILS COMPLETS'}
        </Box>
      </Button>

      {/* Détails supplémentaires */}
      <Collapse in={showDetails} style={{ marginTop: theme.spacing(2) }}>
        {/* Top 10 des meilleurs clients */}
        {data.top_clients && data.top_clients.length > 0 && (
          <DataTable
            title="🏆 TOP 10 DES MEILLEURS CLIENTS"
            data={data.top_clients}
            columns={['Client', 'Nombre de Bons', 'Montant Total', 'Payé', 'Impayé', 'Dernière Commande']}
            fieldMapping={['nom_client', 'nombre_bons', 'montant_total', 'montant_paye', 'montant_impaye', 'derniere_commande']}
          />
        )}

        {/* Répartition par type de client */}
        {data.repartition_par_type && data.repartition_par_type.length > 0 && (
          <DataTable
            title="📊 RÉPARTITION PAR TYPE DE CLIENT"
            data={data.repartition_par_type}
            columns={['Type Client', 'Nombre de Bons', 'Montant Total', 'Payé', 'Impayé']}
            fieldMapping={['type_client', 'nombre_bons', 'montant_total', 'montant_paye', 'montant_impaye']}
          />
        )}

        {/* Évolution mensuelle */}
        {data.evolution_mensuelle && data.evolution_mensuelle.length > 0 && (
          <DataTable
            title="📈 ÉVOLUTION MENSUELLE (12 DERNIERS MOIS)"
            data={data.evolution_mensuelle}
            columns={['Mois', 'Nombre de Bons', 'Montant Total', 'Payé', 'Impayé']}
            fieldMapping={['mois', 'nombre_bons', 'montant_total', 'montant_paye', 'montant_impaye']}
          />
        )}
      </Collapse>

      {/* Pied de page / métriques secondaires */}
      <Paper className={classes.footerMetrics} elevation={0}>
        <Typography variant="h5" className={classes.sectionTitle} style={{ color: '#f26522' }}>
          ANALYSE ET INDICATEURS
        </Typography>
        <Grid container alignItems="center" justifyContent="space-around" spacing={2}>
          <Grid item xs={12} md={4} style={{ textAlign: 'center', padding: theme.spacing(2) }}>
            <Typography variant="h3" className={classes.metricValue} style={{ color: '#f44336' }}>
              {data.taux_impayes || '0'}%
            </Typography>
            <Typography variant="body1" className={classes.metricLabel}>
              Taux d'impayés
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: theme.spacing(1) }}>
              Proportion des montants non payés
            </Typography>
          </Grid>
          <Divider orientation="vertical" flexItem style={{ backgroundColor: '#e0e0e0', height: '60px' }} />
          <Grid item xs={12} md={4} style={{ textAlign: 'center', padding: theme.spacing(2) }}>
            <Typography variant="h3" className={classes.metricValue} style={{ color: '#226D68' }}>
              {data.totaux?.global?.nombre_bons > 0 
                ? `${formatCurrency(parseFloat(data.totaux.global.montant_total) / data.totaux.global.nombre_bons)}`
                : '0,00'} F CFA
            </Typography>
            <Typography variant="body1" className={classes.metricLabel}>
              Panier moyen
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: theme.spacing(1) }}>
              Montant moyen par bon
            </Typography>
          </Grid>
          <Divider orientation="vertical" flexItem style={{ backgroundColor: '#e0e0e0', height: '60px' }} />
          <Grid item xs={12} md={4} style={{ textAlign: 'center', padding: theme.spacing(2) }}>
            <Typography variant="h3" className={classes.metricValue} style={{ color: '#2E7D32' }}>
              {data.totaux?.global?.nombre_bons || 0}
            </Typography>
            <Typography variant="body1" className={classes.metricLabel}>
              Total bons vendus
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: theme.spacing(1) }}>
              Nombre total de transactions
            </Typography>
          </Grid>
        </Grid>
        
        {/* Dates importantes */}
        <Grid container spacing={3} style={{ marginTop: theme.spacing(4) }}>
          <Grid item xs={12} md={6}>
            <Paper className={classes.amountCard} elevation={0} style={{ background: '#E3F2FD' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarToday style={{ marginRight: 12, color: '#1976D2', fontSize: 28 }} />
                <Typography variant="body1" style={{ color: '#1976D2', fontWeight: 600 }}>
                  Première Commande
                </Typography>
              </Box>
              <Typography variant="h4" style={{ fontWeight: 700, color: '#1976D2' }}>
                {formatDate(data.totaux?.payes?.date_premier)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper className={classes.amountCard} elevation={0} style={{ background: '#E8F5E9' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarToday style={{ marginRight: 12, color: '#2E7D32', fontSize: 28 }} />
                <Typography variant="body1" style={{ color: '#2E7D32', fontWeight: 600 }}>
                  Dernière Commande
                </Typography>
              </Box>
              <Typography variant="h4" style={{ fontWeight: 700, color: '#2E7D32' }}>
                {formatDate(data.totaux?.payes?.date_dernier)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          style={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BonClientStatsDashboard;